import React, { useState, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import '../styles/kontakt.css';
import contactBackgroundMobile from '../assets/webp/desktop/Contact1.webp';

const Kontakt = () => {
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Initialize EmailJS
  useLayoutEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  // Preload the background image
  useLayoutEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = contactBackgroundMobile;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formData = new FormData(e.target);

    const templateParams = {
      from_name: formData.get('Ime'),
      from_email: formData.get('Email'),
      subject: selectedTopic === 'Općenito' ? customTopic : selectedTopic,
      message: formData.get('Poruka'),
      to_name: 'Konto Zona Team',
      timestamp: new Date().toLocaleString('hr-HR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams
      );
      
      setSubmitStatus('success');
      setShowThankYouModal(true);
      e.target.reset();
      setSelectedTopic('');
      setCustomTopic('');
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTopicChange = (e) => {
    setSelectedTopic(e.target.value);
    if (e.target.value !== 'Općenito') {
      setCustomTopic('');
    }
  };





  return (
    <>
      <Helmet>
        <title>Kontakt Instrukcije | Rezervirajte Termin - Konto Zona Zagreb</title>
        <meta name="description" content="Kontaktirajte Konto Zona za rezervaciju termina instrukcija iz računovodstva, ekonomije, statistike i matematike. Dostupni smo online putem WhatsAppa i e‑maila." />
        <meta name="keywords" content="kontakt instrukcije Zagreb, rezervacija termina instrukcija, WhatsApp instrukcije, email instrukcije, adresa Konto Zona, kontakt instrukcije računovodstvo, kontakt instrukcije ekonomija" />
        <meta property="og:title" content="Kontakt Instrukcije | Rezervirajte Termin - Konto Zona Zagreb" />
        <meta property="og:description" content="Kontaktirajte nas za rezervaciju termina instrukcija računovodstvo, instrukcija ekonomija ili naručivanje skripti. WhatsApp, email ili kontakt forma." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://instrukcijekontozona.hr/kontakt" />
        <meta property="og:image" content="https://instrukcijekontozona.hr/kontozona-logo.png" />
        <meta property="og:locale" content="hr_HR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kontakt Instrukcije | Rezervirajte Termin - Konto Zona Zagreb" />
        <meta name="twitter:description" content="Kontaktirajte nas za rezervaciju termina instrukcija računovodstvo, instrukcija ekonomija ili naručivanje skripti. WhatsApp, email ili kontakt forma." />
        <meta name="twitter:image" content="https://instrukcijekontozona.hr/kontozona-logo.png" />
        <link rel="canonical" href="https://instrukcijekontozona.hr/kontakt" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Kontakt - Konto Zona",
            "description": "Kontaktirajte Konto Zona za rezervaciju termina instrukcija",
            "mainEntity": {
              "@type": "Organization",
              "name": "Konto Zona",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+385-97-666-6639",
                "contactType": "customer service",
                "email": "konto.zona@gmail.com",
                "availableLanguage": "hr",
                "areaServed": "HR"
              }
            }
          })}
        </script>
      </Helmet>
    <section 
      className={`section contact-section-with-background ${imageLoaded ? 'background-loaded' : 'background-loading'}`}
      style={{
        backgroundImage: imageLoaded ? `url(${contactBackgroundMobile})` : 'none',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundAttachment: 'scroll',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        zIndex: 10,
        backgroundColor: imageLoaded ? 'transparent' : '#f8f9fa',
      }}
    >
      <div className="container">
        <div className="section-title">
          <h1 style={{ color: "white", textShadow: "2px 2px 4px rgba(0, 0, 0, 0.7)" }}>Kontakt - Rezervirajte Instrukcije</h1>
          <p style={{ fontSize: "21px" }}>Želiš rezervirati termin ili naručiti skriptu? Javi nam se.</p>
        </div>

        <div id="form" className="contact-grid">
          <div className="contact-info">
            <div className="contact-logo">
              <img src="/kontozona-logo.png" alt="Konto Zona Logo" />
            </div>
            <h3>Kontaktirajte nas</h3>
            <p className="contact-message">Najbrži način komunikacije putem WhatsApp poruke</p>
            
            <div className="contact-info-item">
              <a 
                href="tel:+385976666639"
                className="phone-link"
              >
                <i className="fas fa-phone"></i>
                <span>Nazovite nas</span>
              </a>
            </div>

            <div className="contact-info-item">
              <a 
                href="https://wa.me/385976666639" 
                target="_blank" 
                rel="noopener noreferrer"
                className="whatsapp-link"
              >
                <i className="fab fa-whatsapp"></i>
                <span>Pošalji nam poruku</span>
              </a>
            </div>

            <div className="contact-info-item">
              <a 
                href="mailto:konto.zona@gmail.com"
                className="email-link"
              >
                <i className="fas fa-envelope"></i>
                <span>konto.zona@gmail.com</span>
              </a>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="contact-form">
            <h3>Ili pošalji upit putem forme</h3>
            <div className="form-group">
              <input 
                type="text" 
                name="Ime" 
                placeholder="Vaše ime i prezime" 
                required 
              />
            </div>
            <div className="form-group">
              <input 
                type="email" 
                name="Email" 
                placeholder="Vaša email adresa" 
                required 
              />
            </div>
            <div className="form-group">
              <select 
                name="TemaUpita" 
                value={selectedTopic}
                onChange={handleTopicChange}
                required
              >
                <option value="" disabled>Odaberite temu upita...</option>
                <option value="Upit za individualne instrukcije">Upit za individualne instrukcije</option>
                <option value="Upit za grupne instrukcije">Upit za grupne instrukcije</option>
                <option value="Upit za skriptu">Upit za skriptu</option>
                <option value="Općenito">Općenito pitanje</option>
              </select>
            </div>
            
            {selectedTopic === 'Općenito' && (
              <div className="form-group">
                <input 
                  type="text" 
                  name="CustomTopic" 
                  placeholder="Napišite naslov teme..." 
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="form-group">
              <textarea 
                name="Poruka" 
                rows="6" 
                placeholder="Opišite detaljno o čemu se radi..." 
                required
              ></textarea>
            </div>

            <button 
              type="submit" 
              className={`btn btn-primary ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Slanje...' : 'Pošalji poruku'}
            </button>
            

            
            {submitStatus === 'error' && (
              <div className="form-error">
                <i className="fas fa-exclamation-circle"></i>
                <p>Došlo je do greške pri slanju poruke. Molimo pokušajte ponovno ili kontaktirajte nas putem WhatsApp-a.</p>
              </div>
            )}
          </form>
        </div>
      </div>
      
      {/* Modal zahvale */}
      {showThankYouModal && (
        <div className="thank-you-modal-overlay" onClick={() => setShowThankYouModal(false)}>
          <div className="thank-you-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button 
                className="modal-close-btn"
                onClick={() => setShowThankYouModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              
              <h3>Hvala vam!</h3>
              <p>Vaša poruka je uspješno poslana. Odgovorit ćemo vam u najkraćem mogućem roku.</p>
              
              <div className="contact-alternatives">
                <p>U međuvremenu možete nas kontaktirati i putem:</p>
                <div className="contact-buttons">
                  <a 
                    href="https://wa.me/385976666639" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="whatsapp-btn"
                  >
                    <i className="fab fa-whatsapp"></i>
                    WhatsApp
                  </a>
                  <a 
                    href="tel:+385976666639"
                    className="phone-btn"
                  >
                    <i className="fas fa-phone"></i>
                    Nazovite nas
                  </a>
                </div>
              </div>
              
              <button 
                className="modal-close-button"
                onClick={() => setShowThankYouModal(false)}
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
    </>
  );
};

export default Kontakt; 